import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Scale, Building2, FileText, CheckCircle2, AlertTriangle } from "lucide-react";
import Eyebrow from "@/components/Eyebrow";
import { FadeUp, Stagger } from "@/components/Motion";
import ThreeWayReconciliation from "@/components/ThreeWayReconciliation";
import CTABlock from "@/components/CTABlock";

const services = [
  { title: "Monthly bookkeeping & financial organization", body: "Structured monthly close with documented workflows. Your numbers, on time and on purpose." },
  { title: "Trust account management (IOLTA compliant)", body: "Dedicated trust ledger management aligned with state bar rules." },
  { title: "3-way reconciliation", body: "Bank, book, and client ledgers, verified to match every single month." },
  { title: "Financial reporting you can actually use", body: "P&L, cash flow, and partner-level clarity, written for decisions, not accountants." },
  { title: "Cleanup & catch-up work", body: "We inherit messy books and bring them to audit-readiness without drama." },
  { title: "Ongoing accuracy & oversight", body: "A second set of trained eyes on every month. Nothing slips through." },
];

const segments = [
  {
    icon: Scale,
    title: "Trust Attorneys",
    body: "You're managing client funds daily. Precision isn't optional, it's required. We ensure every dollar is accounted for and properly tracked.",
    to: "/trust-attorneys",
  },
  {
    icon: Building2,
    title: "Real Estate Attorneys",
    body: "High transaction volume, moving money, tight timelines. We keep everything aligned so nothing slips through the cracks.",
    to: "/real-estate-attorneys",
  },
  {
    icon: FileText,
    title: "Corporate & Business",
    body: "Your firm needs clean financials to support growth, planning, and decision-making. We bring clarity so you can move with confidence.",
    to: "/corporate-attorneys",
  },
];

const fallShort = [
  "Proper trust accounting",
  "Accurate client-level tracking",
  "Clean reconciliation processes",
  "Systems that hold up under scrutiny",
];

const standards = [
  "Structured workflows",
  "Documented processes",
  "Consistent reconciliation",
  "Clear reporting",
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden isolate" data-testid="hero-section">
        {/* Waving US flag background */}
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          aria-hidden="true"
          data-testid="hero-flag-bg"
        >
          <img
            src="/brand/usa-flag-waving.gif"
            alt=""
            className="w-full h-full object-cover opacity-[0.55]"
          />
          {/* Ivory wash, softer so the flag is clearly visible */}
          <div className="absolute inset-0 bg-[#FDFBF7]/45" />
          {/* Left-side heavy fade so the headline sits on clean ivory */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#FDFBF7] via-[#FDFBF7]/80 to-transparent" />
          {/* Top/bottom fade to blend into the page */}
          <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-[#FDFBF7] to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#FDFBF7] to-transparent" />
        </div>

        <div className="container-editorial pt-12 md:pt-20 pb-24 md:pb-32 relative">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-end">
            <div className="md:col-span-8">
              <Eyebrow>Bookkeeping for Law Firms</Eyebrow>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-[2.6rem] sm:text-5xl md:text-6xl lg:text-[5.25rem] leading-[0.98] tracking-tight mt-7 text-[#0C1D36]"
              >
                Bookkeeping Built for{" "}
                <span className="italic text-[#BA1A1A]">Attorneys</span>{" "}
                <br className="hidden md:block" />
                Who Can't Afford Mistakes.
              </motion.h1>
              <FadeUp delay={0.15} className="mt-8 text-lg md:text-xl text-[#0C1D36]/80 leading-relaxed max-w-2xl">
                Your financials aren't just numbers, they're tied to compliance, trust
                accounts, and real risk. We help law firms stay organized, accurate, and
                protected.
              </FadeUp>
              <FadeUp delay={0.25} className="mt-10 flex flex-wrap items-center gap-5">
                <Link to="/contact" className="btn-primary" data-testid="hero-primary-cta">
                  Request a Financial Review
                  <ArrowUpRight className="w-4 h-4" />
                </Link>
                <Link to="/trust-compliance" className="btn-ghost" data-testid="hero-secondary-cta">
                  See the 3-way reconciliation standard
                </Link>
              </FadeUp>
            </div>

            <div className="md:col-span-4">
              <FadeUp delay={0.3} className="relative">
                <div className="aspect-[4/5] bg-[#F3EFE9] overflow-hidden border border-[#0C1D36]/10 relative">
                  <img
                    src="https://images.pexels.com/photos/9367109/pexels-photo-9367109.jpeg"
                    alt="Classical law firm exterior"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0C1D36]/25 to-transparent" />
                </div>
                <div className="absolute -left-4 -bottom-4 w-24 h-24 bg-[#BA1A1A] hidden md:block" aria-hidden="true" />
                <div className="absolute top-5 -right-3 bg-[#0C1D36] text-[#FDFBF7] px-4 py-3 text-[10px] uppercase tracking-[0.22em] hidden md:block">
                  Audit-Ready
                </div>
              </FadeUp>
            </div>
          </div>

          {/* Trust indicators */}
          <FadeUp delay={0.4} className="mt-20 pt-8 border-t border-[#0C1D36]/15 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              ["IOLTA", "Compliant bookkeeping"],
              ["3-Way", "Monthly reconciliation"],
              ["100%", "Client ledger accuracy"],
              ["0", "Tolerance for error"],
            ].map(([big, small]) => (
              <div key={big}>
                <div className="font-serif text-4xl md:text-5xl text-[#0C1D36]">{big}</div>
                <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[#4B5563]">
                  {small}
                </div>
              </div>
            ))}
          </FadeUp>
        </div>
      </section>

      {/* PROBLEM FRAMING */}
      <section className="bg-[#F3EFE9] rule-top rule-bottom" data-testid="problem-section">
        <div className="container-editorial py-24 md:py-32 grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Eyebrow muted>The Reality</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl leading-tight tracking-tight text-[#0C1D36] mt-6">
              You didn't start your firm to manage reconciliations.
            </h2>
          </div>
          <div className="md:col-span-8 md:border-l md:border-[#0C1D36]/15 md:pl-12">
            <p className="font-serif text-2xl md:text-[1.7rem] leading-snug text-[#0C1D36] max-w-2xl">
              But the reality is, when bookkeeping is off,{" "}
              <em className="text-[#BA1A1A] not-italic">everything feels off.</em>
            </p>
            <div className="mt-10 space-y-6 max-w-xl">
              <p className="text-lg text-[#0C1D36]/80">Missed details turn into bigger problems.</p>
              <p className="text-lg text-[#0C1D36]/80">Unclear numbers slow down decisions.</p>
              <p className="text-lg text-[#0C1D36]/80">
                And trust accounts? <span className="font-semibold">There's no room for error.</span>
              </p>
            </div>
            <p className="mt-10 font-serif italic text-xl text-[#0C1D36]/90">
              That's where we come in.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE ACTUALLY DO */}
      <section className="container-editorial py-24 md:py-32" data-testid="services-section">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <Eyebrow>What We Actually Do</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#0C1D36] mt-6">
              We don't just "handle your books."
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg text-[#0C1D36]/75 max-w-xl">
              We bring structure, clarity, and control to your financial systems, so your
              firm operates on numbers you can defend.
            </p>
          </div>
        </div>

        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 border-t border-l border-[#0C1D36]/12">
          {services.map((s, i) => (
            <FadeUp
              key={s.title}
              className="border-r border-b border-[#0C1D36]/12 p-8 md:p-10 group transition-colors hover:bg-[#F3EFE9]"
              data-testid={`service-card-${i}`}
            >
              <div className="flex items-start gap-3">
                <span className="eyebrow-muted pt-1">0{i + 1}</span>
              </div>
              <h3 className="font-serif text-xl md:text-2xl text-[#0C1D36] mt-4 leading-snug">
                {s.title}
              </h3>
              <p className="text-[15px] text-[#4B5563] mt-4 leading-relaxed">{s.body}</p>
              <div className="h-px w-8 bg-[#BA1A1A] mt-6 transition-all group-hover:w-16" />
            </FadeUp>
          ))}
        </Stagger>
      </section>

      {/* SEGMENTS */}
      <section className="bg-[#0C1D36] text-[#FDFBF7] noise" data-testid="segments-section">
        <div className="container-editorial py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <Eyebrow className="text-white">Who We Work With</Eyebrow>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] mt-6">
                Built around how your firm actually operates.
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-6">
              <p className="text-lg text-white/70 max-w-xl">
                Three segments. Three distinct financial realities. One standard of
                precision.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-white/10">
            {segments.map((seg, i) => {
              const Icon = seg.icon;
              return (
                <Link
                  key={seg.title}
                  to={seg.to}
                  className="group bg-[#0C1D36] p-10 hover:bg-[#0A1829] transition-colors relative"
                  data-testid={`segment-card-${i}`}
                >
                  <div className="flex items-start justify-between">
                    <Icon className="w-8 h-8 text-[#BA1A1A]" />
                    <ArrowUpRight className="w-5 h-5 text-white/50 group-hover:text-[#FDFBF7] group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-8 leading-tight">
                    {seg.title}
                  </h3>
                  <p className="mt-5 text-white/70 leading-relaxed text-[15px]">{seg.body}</p>
                  <span className="mt-10 inline-block text-xs uppercase tracking-[0.22em] text-white/50 group-hover:text-[#BA1A1A] transition-colors">
                    Explore segment →
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CORE DIFFERENTIATOR */}
      <section className="container-editorial py-24 md:py-32" data-testid="differentiator-section">
        <div className="grid md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-5">
            <Eyebrow>Where Most Bookkeeping Falls Short</Eyebrow>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#0C1D36] mt-6">
              Categorizing transactions isn't enough for a law firm.
            </h2>
          </div>
          <div className="md:col-span-7 md:pt-6">
            <p className="text-lg text-[#0C1D36]/75 max-w-xl">
              Most bookkeepers stop at clean categories. That leaves trust gaps, ledger
              drift, and reconciliation failure modes a partner never wants to discover in
              a bar audit.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-[#0C1D36]/12">
          <div className="bg-[#FDFBF7] p-8 md:p-12">
            <div className="flex items-center gap-2 eyebrow-muted mb-6">
              <AlertTriangle className="w-4 h-4 text-[#BA1A1A]" />
              What firms actually need
            </div>
            <ul className="space-y-5">
              {fallShort.map((f) => (
                <li key={f} className="flex items-start gap-4 font-serif text-xl md:text-2xl text-[#0C1D36]">
                  <span className="text-[#BA1A1A] mt-2 w-2 h-2 bg-[#BA1A1A] rounded-full flex-shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-[#0C1D36] text-[#FDFBF7] p-8 md:p-12">
            <div className="flex items-center gap-2 eyebrow-muted text-white/70 mb-6">
              <CheckCircle2 className="w-4 h-4 text-[#BA1A1A]" />
              Our Standard · Every client
            </div>
            <ul className="space-y-5">
              {standards.map((s) => (
                <li key={s} className="flex items-start gap-4 font-serif text-xl md:text-2xl">
                  <span className="text-[#BA1A1A] mt-2 w-2 h-2 bg-[#BA1A1A] rounded-full flex-shrink-0" />
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-10 font-serif italic text-lg text-white/80">
              Because accuracy isn't a feature, it's the baseline.
            </p>
          </div>
        </div>
      </section>

      {/* 3-WAY RECONCILIATION */}
      <section className="bg-[#F3EFE9] rule-top rule-bottom" data-testid="reconciliation-section">
        <div className="container-editorial py-24 md:py-32">
          <div className="grid md:grid-cols-12 gap-10 mb-14">
            <div className="md:col-span-5">
              <Eyebrow>The Standard</Eyebrow>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] text-[#0C1D36] mt-6">
                3-Way Reconciliation, every single month.
              </h2>
            </div>
            <div className="md:col-span-7 md:pt-6">
              <p className="text-lg text-[#0C1D36]/75 max-w-xl">
                Bank balance. Book balance. Individual client ledgers. If all three don't
                match, you have a problem. We make sure they do.
              </p>
              <Link to="/trust-compliance" className="btn-ghost mt-6 inline-flex" data-testid="view-reconciliation-link">
                Review the full methodology
              </Link>
            </div>
          </div>

          <ThreeWayReconciliation />
        </div>
      </section>

      <CTABlock
        eyebrow={null}
        title="Want a clearer picture of how your books are being handled, and where things can be stronger?"
        body="We'll walk through it with you. No sales pressure, no obligation. Just a clean review."
      />
    </div>
  );
}
